import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DashboardPanel, EmployeeHoursRow, inputStyle } from './DashboardUI';
import { Loader2, Download, Mail } from 'lucide-react';

const HoursTab = ({
  // Break & Workday settings
  customization, setCustomization, saveSingleSetting,
  // Staff hours
  staffHours, staffHoursLoading, staffHoursPeriod, setStaffHoursPeriod,
  // Automated email
  weeklyHoursEmail, setWeeklyHoursEmail, weeklyHoursEmailLoading, saveWeeklyHoursEmail,
  // Downloads
  downloadDaysReport, downloadThisMonthReport, downloadMonthReport,
  availableMonths, selectedMonth, setSelectedMonth,
  isLoading,
}) => (
  <div className="space-y-10" data-testid="hours-tab">
    {/* Break & Workday Settings */}
    <DashboardPanel title="Break & Workday Settings">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Standard Workday Hours</label>
          <Input
            type="number" step="0.5" min="1" max="24"
            value={customization.workday_hours || 8}
            onChange={(e) => setCustomization({...customization, workday_hours: parseFloat(e.target.value)})}
            onBlur={(e) => saveSingleSetting('workday_hours', parseFloat(e.target.value))}
            className={inputStyle}
          />
        </div>
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Break Duration (minutes)</label>
          <Input
            type="number" min="0" max="120"
            value={customization.standard_break_minutes || 30}
            onChange={(e) => setCustomization({...customization, standard_break_minutes: parseInt(e.target.value)})}
            onBlur={(e) => saveSingleSetting('standard_break_minutes', parseInt(e.target.value))}
            className={inputStyle}
          />
        </div>
      </div>

      {/* Auto Break Deduction Toggle */}
      <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-950/30 px-5 py-4">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="text-sm font-medium text-white">Enable automatic break deduction</div>
            <div className="mt-1 text-xs text-zinc-400">Break time will be deducted automatically for shifts that meet your workday threshold.</div>
          </div>
          <div className="pt-1">
            <Switch
              checked={customization.break_deduction_enabled || false}
              onCheckedChange={async (checked) => {
                setCustomization(prev => ({...prev, break_deduction_enabled: checked}));
                await saveSingleSetting('break_deduction_enabled', checked);
              }}
            />
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900/40 px-4 py-3 text-xs text-zinc-300">
          Note: Break time is only deducted from shifts that meet or exceed the standard workday hours.
        </div>
      </div>
    </DashboardPanel>

    {/* Weekly Staff Hours Overview */}
    <DashboardPanel title="Weekly Staff Hours Overview">
      <div className="flex flex-wrap items-center gap-2 pb-4">
        {['this_week', 'last_week', 'this_month'].map((period) => (
          <Button
            key={period}
            type="button"
            onClick={() => setStaffHoursPeriod(period)}
            className={`rounded-xl px-4 py-2 text-sm ${
              staffHoursPeriod === period
                ? "bg-violet-500/20 text-violet-200 hover:bg-violet-500/30"
                : "bg-zinc-800 text-zinc-200 hover:bg-zinc-700"
            }`}
          >
            {period === 'this_week' ? 'This week' : period === 'last_week' ? 'Last week' : 'This month'}
          </Button>
        ))}
      </div>

      <div className="space-y-2">
        {staffHoursLoading ? (
          <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" /></div>
        ) : staffHours.length > 0 ? (
          staffHours.map((emp, idx) => (
            <EmployeeHoursRow
              key={idx}
              name={emp.name || `${emp.first_name} ${emp.last_name}`}
              hours={`${typeof emp.total_hours === 'number' ? emp.total_hours.toFixed(1) : emp.total_hours || 0}h`}
            />
          ))
        ) : (
          <p className="text-sm text-zinc-400 text-center py-4">No hours data for this period</p>
        )}
      </div>
    </DashboardPanel>

    {/* Automated Report Email */}
    <DashboardPanel title="Automated Report Email">
      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          <button
            onClick={() => setWeeklyHoursEmail({...weeklyHoursEmail, frequency: 'weekly'})}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              weeklyHoursEmail.frequency === 'weekly'
                ? 'bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/25'
                : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200'
            }`}
            data-testid="frequency-weekly-btn"
          >Weekly</button>
          <button
            onClick={() => setWeeklyHoursEmail({...weeklyHoursEmail, frequency: 'monthly'})}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              weeklyHoursEmail.frequency === 'monthly'
                ? 'bg-violet-500/20 text-violet-200 ring-1 ring-violet-400/25'
                : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200'
            }`}
            data-testid="frequency-monthly-btn"
          >Monthly</button>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <Input
            type="email"
            placeholder="manager@email.com"
            value={weeklyHoursEmail.email_address || ''}
            onChange={(e) => setWeeklyHoursEmail({...weeklyHoursEmail, email_address: e.target.value})}
            className={`${inputStyle} flex-1`}
            data-testid="report-email-input"
          />
          {weeklyHoursEmail.frequency === 'weekly' && (
            <Select value={weeklyHoursEmail.delivery_day || 'friday'} onValueChange={(val) => setWeeklyHoursEmail({...weeklyHoursEmail, delivery_day: val})}>
              <SelectTrigger className={`w-full md:w-40 ${inputStyle}`} data-testid="delivery-day-select">
                <SelectValue placeholder="Select day" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="friday">Friday</SelectItem>
                <SelectItem value="saturday">Saturday</SelectItem>
                <SelectItem value="sunday">Sunday</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={saveWeeklyHoursEmail}
            disabled={weeklyHoursEmailLoading}
            className="bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 rounded-xl whitespace-nowrap"
            data-testid="save-report-email-btn"
          >
            <Mail className="mr-2 h-4 w-4" /> {weeklyHoursEmailLoading ? 'Saving...' : 'Set Email'}
          </Button>
        </div>
        {weeklyHoursEmail.exists && (
          <p className="text-xs text-zinc-400">
            {weeklyHoursEmail.frequency === 'monthly'
              ? `Monthly report will be sent to ${weeklyHoursEmail.email_address} on the 1st of each month`
              : `Weekly report will be sent to ${weeklyHoursEmail.email_address} every ${weeklyHoursEmail.delivery_day}`
            }
          </p>
        )}
      </div>
    </DashboardPanel>

    {/* Download Reports */}
    <DashboardPanel title="Download Reports">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Button onClick={() => downloadDaysReport(7)} disabled={isLoading} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-xl h-auto py-4" data-testid="download-7-days-btn">
          <div className="text-center"><Download className="h-5 w-5 mx-auto mb-2" /><span>Previous 7 Days</span></div>
        </Button>
        <Button onClick={downloadThisMonthReport} disabled={isLoading} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-xl h-auto py-4" data-testid="download-this-month-btn">
          <div className="text-center"><Download className="h-5 w-5 mx-auto mb-2" /><span>This Month</span></div>
        </Button>
      </div>

      {availableMonths.length > 0 && (
        <div className="mt-6 pt-6 border-t border-zinc-800">
          <h3 className="text-sm font-medium text-white mb-4">Monthly Report</h3>
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className={`w-full md:w-64 ${inputStyle}`}><SelectValue placeholder="Select month" /></SelectTrigger>
              <SelectContent>
                {availableMonths.map((month) => (
                  <SelectItem key={month.value} value={month.value}>{month.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={downloadMonthReport} disabled={!selectedMonth || isLoading} className="bg-violet-500/20 text-violet-200 hover:bg-violet-500/30 rounded-xl">
              <Download className="mr-2 h-4 w-4" /> Download
            </Button>
          </div>
        </div>
      )}
    </DashboardPanel>
  </div>
);

export default HoursTab;
